/**
 * @file Shared HTTP route contracts for the admin daemon's Emails
 *       sub-app surface (`/api/emails/*`) — the operator-facing wire the
 *       `admin.emails` client + the Emails UI consume (#1598 Parts 7–9).
 * @module @labcraft/admin/shared/emails_routes
 *
 * The admin daemon is a session-gated proxy in front of two loopback
 * daemons: it resolves a recipient subset via `@labcraft/account` and
 * hands the composed broadcast to `@labcraft/email`'s bulk/campaign
 * surface. These routes are the browser-facing contracts for that
 * proxy; the payload shapes mirror the email daemon's wire types
 * (`CampaignSummaryWire`, `CampaignDetailWire`, `SuppressionWire`) so the
 * admin daemon can re-validate and re-emit what the email daemon
 * returned — the same mirror pattern `routes.ts` uses for the account
 * daemon's `Learner`.
 *
 * Deliberately NOT importing the email daemon's Zod schemas: those are
 * package-internal to `@labcraft/email` (only its wire *types* are
 * exported), and `defineRoute` needs Zod objects. Mirroring here keeps
 * the admin surface self-contained and keeps `src/shared/**` free of any
 * cross-package runtime import (dependency-cruiser invariant).
 *
 * Lives in `shared/` (zod + `defineRoute` + `CoreAnalytics` schema only,
 * no `node:*` / DOM globals) so the service (`src/service/emails.ts`),
 * the client (`src/client/emails.ts`), and the UI all reference one
 * contract.
 */

import { z } from 'zod'
import { CoreAnalytics, defineRoute } from '@labcraft/core'

////////////////////////////////////////////////////////////////////////////////
// Recipient-subset filters (mirror @labcraft/account's LearnerFilterSchema)
////////////////////////////////////////////////////////////////////////////////

/**
 * Composable recipient-subset filters, mirroring the account daemon's
 * `LearnerFilterSchema` (#1598 Part 6). Every field optional; the set
 * composes as AND. The compose surface builds one of these to preview a
 * recipient count and to scope a filter-based send. Kept structurally
 * identical to the account filter so admin can forward it verbatim.
 *
 * @property status - Access-state filter (`active` / `inactive` /
 *   `banned`). Omit for "all".
 * @property q - Case-insensitive substring match on email or display
 *   name.
 * @property signedUpAfter - Inclusive lower bound on signup time
 *   (ISO-8601 datetime).
 * @property signedUpBefore - Inclusive upper bound on signup time
 *   (ISO-8601 datetime).
 * @property timezone - Exact match on the learner's captured IANA zone.
 * @property acquisitionSource - Exact match on the granular acquisition
 *   source token.
 * @property acquisitionCategory - Exact match on the coarse acquisition
 *   category (`campaign` / `organic` / `referral` / `direct`).
 * @property hasProgress - `true` keeps learners who have started a
 *   course; `false` keeps those who never have (re-engagement cohort);
 *   absent applies no progress filter.
 * @property tags - Tag INCLUDE filter (#1639): keep learners carrying any
 *   of the listed tags (union). Admin forwards these RAW to the account
 *   daemon (the sole canonicalizer) — no `LearnerTagSchema` here. This is
 *   the highest-stakes targeting knob: a tag-scoped send must stay
 *   tag-scoped, so it rides the resolve + send filter set verbatim.
 *   Empty/absent = no constraint (never match-nobody).
 * @property tagsNot - Tag EXCLUDE filter (#1639): drop learners carrying
 *   any of the listed tags (none-of). Same raw-forward + empty semantics
 *   as `tags`.
 */
export const EmailRecipientFilterSchema = z.object({
  status: z.enum(['active', 'inactive', 'banned']).optional(),
  q: z.string().trim().min(1).optional(),
  signedUpAfter: z.string().datetime().optional(),
  signedUpBefore: z.string().datetime().optional(),
  timezone: z.string().trim().min(1).optional(),
  acquisitionSource: z.string().trim().min(1).optional(),
  acquisitionCategory: CoreAnalytics.AcquisitionCategorySchema.optional(),
  hasProgress: z.boolean().optional(),
  tags: z.array(z.string()).optional(),
  tagsNot: z.array(z.string()).optional(),
})

/** Parsed recipient-subset filter set. */
export type EmailRecipientFilters = z.infer<typeof EmailRecipientFilterSchema>

////////////////////////////////////////////////////////////////////////////////
// POST /api/emails/recipients/count — authoritative preview count
////////////////////////////////////////////////////////////////////////////////

/**
 * Response of `POST /api/emails/recipients/count`. The exact number of
 * learners the filter selects over the whole `learners` table — the
 * authoritative preview the compose surface shows before a send, so the
 * previewed count equals the persisted campaign snapshot (#1598 Part 7).
 *
 * @property count - Exact recipient count for the filter set.
 */
export const ResolveRecipientsResponseSchema = z.object({
  count: z.number().int().nonnegative(),
})

/** Parsed recipient-count response. */
export type ResolveRecipientsResponse = z.infer<
  typeof ResolveRecipientsResponseSchema
>

/**
 * `POST /api/emails/recipients/count` — resolve a filter set to its
 * exact recipient count (via the account daemon's resolve route). Body
 * is the {@link EmailRecipientFilterSchema} filter set. Session-gated;
 * 401 for anonymous.
 */
export const ResolveRecipientsRoute = defineRoute({
  method: 'POST',
  path: '/api/emails/recipients/count',
  request: EmailRecipientFilterSchema,
  response: ResolveRecipientsResponseSchema,
  description: 'Resolve a recipient-filter set to its exact count (preview).',
})

////////////////////////////////////////////////////////////////////////////////
// POST /api/emails/send — compose + send a broadcast
////////////////////////////////////////////////////////////////////////////////

/**
 * Body of `POST /api/emails/send`. The composed broadcast plus the
 * recipient scope. Two scoping modes:
 *
 *   - **Filter-based** (mass send): `filters` selects the recipients;
 *     the daemon resolves the complete authoritative set via the account
 *     resolve route (never a capped page).
 *   - **Explicit** (multi-select / the Learners deep-link): a non-empty
 *     `recipientIds` list names exactly which learners to mail. `filters`
 *     is ignored in this mode.
 *
 * @property subject - Operator-composed subject (no merge variables).
 * @property body - Operator-composed plaintext body (no merge
 *   variables).
 * @property filters - Recipient-subset filter set for filter-based
 *   sends. `{}` selects every learner; the compose surface's count
 *   preview makes the size visible before send.
 * @property recipientIds - Optional explicit learner-id list. When
 *   present and non-empty, the send targets exactly these learners and
 *   `filters` is ignored — this backs the compose multi-select and the
 *   single-recipient deep-link from the Learners panel. A size-1 list is how a
 *   1:1 message is sent (the retired `individual` kind, #1619).
 * @property classification - Send-type lane (#1616): `transactional` (SMTP,
 *   `@labcraft.dev`, no footer, sent regardless of unsubscription) vs
 *   `marketing` (Resend, `@news.labcraft.dev`, unsubscribe footer,
 *   auto-suppression). Orthogonal to the audience, so a filtered audience can
 *   be a transactional send and a single recipient a marketing one. Absent ⇒
 *   the daemon defaults to `marketing`.
 * @property from - Optional per-send From identity (validated by the daemon
 *   against the resolved lane's allowlist).
 * @property replyTo - Optional per-send Reply-To; defaults to the daemon's
 *   `default_reply_to` setting.
 */
export const SendBroadcastRequestSchema = z.object({
  subject: z.string().min(1).max(200),
  body: z.string().min(1).max(200_000),
  format: z.enum(['text', 'html']).optional(),
  filters: EmailRecipientFilterSchema,
  recipientIds: z.array(z.string().uuid()).optional(),
  classification: z.enum(['transactional', 'marketing']).optional(),
  from: z.string().min(1).max(320).optional(),
  replyTo: z.string().min(1).max(320).optional(),
})

/** Parsed `SendBroadcastRequest`. */
export type SendBroadcastRequest = z.infer<typeof SendBroadcastRequestSchema>

/**
 * Response of `POST /api/emails/send` (and the resend route). The created
 * campaign id plus the enqueue tally, mirroring the email daemon's 202
 * `SendBulkResponse` (#1627). Delivery proceeds asynchronously via the
 * outbox worker; the compose UI polls the campaign detail for live counts.
 *
 * @property campaignId - The persisted campaign's id.
 * @property total - Recipients handed to the email daemon.
 * @property suppressed - Dropped as already-suppressed (not enqueued).
 * @property accepted - Recipients enqueued for delivery.
 */
export const SendBroadcastResponseSchema = z.object({
  campaignId: z.string(),
  total: z.number().int().nonnegative(),
  suppressed: z.number().int().nonnegative(),
  accepted: z.number().int().nonnegative(),
})

/** Parsed `SendBroadcastResponse`. */
export type SendBroadcastResponse = z.infer<typeof SendBroadcastResponseSchema>

/**
 * `POST /api/emails/send` — resolve the recipient set (filter-based or
 * explicit), then hand the composed broadcast to the email daemon's bulk
 * fan-out. Blocks until the paced fan-out finishes; returns the tally.
 * Session-gated; 401 for anonymous, 400 when the scope resolves to no
 * recipients.
 */
export const SendBroadcastRoute = defineRoute({
  method: 'POST',
  path: '/api/emails/send',
  request: SendBroadcastRequestSchema,
  response: SendBroadcastResponseSchema,
  description: 'Compose + send a broadcast to a resolved recipient set.',
})

/**
 * Body of `POST /api/emails/test-send` (#1606 Part 13) — mail a preview copy
 * of the composed message to one address only. No campaign, no suppression
 * check, no tracking. `kind` selects the render (campaign chrome + footer, or
 * individual).
 *
 * @property to - The single test address.
 * @property subject - Composed subject.
 * @property body - Composed body (HTML for `format: 'html'`, else plaintext).
 * @property format - `text` or `html`.
 * @property kind - Which render to preview.
 */
export const TestSendRequestSchema = z.object({
  to: z.string().email(),
  subject: z.string().min(1).max(200),
  body: z.string().min(1).max(200_000),
  format: z.enum(['text', 'html']).optional(),
  kind: z.enum(['campaign', 'individual']).optional(),
  from: z.string().min(1).max(320).optional(),
  replyTo: z.string().min(1).max(320).optional(),
})

/** Parsed `TestSendRequest`. */
export type TestSendRequest = z.infer<typeof TestSendRequestSchema>

/** Response of `POST /api/emails/test-send`. */
export const TestSendResponseSchema = z.object({
  status: z.literal('sent'),
  providerMessageId: z.string(),
})

/** Parsed `TestSendResponse`. */
export type TestSendResponse = z.infer<typeof TestSendResponseSchema>

/**
 * `POST /api/emails/test-send` — send a preview copy to one address (no
 * campaign / suppression / tracking). Session-gated.
 */
export const TestSendRoute = defineRoute({
  method: 'POST',
  path: '/api/emails/test-send',
  request: TestSendRequestSchema,
  response: TestSendResponseSchema,
  description: 'Send a preview copy to one address (no campaign / tracking).',
})

////////////////////////////////////////////////////////////////////////////////
// GET /api/emails/senders — selectable sender identities (#1616)
////////////////////////////////////////////////////////////////////////////////

/**
 * One selectable sender identity for the compose From picker (#1616). Mirrors
 * `@labcraft/email`'s `SenderIdentity`.
 *
 * @property label - Human label (e.g. `Support`).
 * @property address - The From address the send goes out as.
 * @property classification - The lane this identity belongs to.
 */
export const SenderIdentitySchema = z.object({
  label: z.string(),
  address: z.string().min(1),
  classification: z.enum(['transactional', 'marketing']),
})

/** Parsed sender identity. */
export type SenderIdentity = z.infer<typeof SenderIdentitySchema>

/**
 * Response of `GET /api/emails/senders` — the selectable identities per lane +
 * the default Reply-To, for the compose From picker.
 *
 * @property senders - Selectable identities (transactional + marketing).
 * @property defaultReplyTo - The default Reply-To (may be empty).
 */
export const ListSendersResponseSchema = z.object({
  senders: z.array(SenderIdentitySchema),
  defaultReplyTo: z.string(),
})

/** Parsed `ListSendersResponse`. */
export type ListSendersResponse = z.infer<typeof ListSendersResponseSchema>

/**
 * `GET /api/emails/senders` — sender identities (per lane) + the default
 * Reply-To. Session-gated.
 */
export const ListSendersRoute = defineRoute({
  method: 'GET',
  path: '/api/emails/senders',
  response: ListSendersResponseSchema,
  description:
    'List selectable sender identities (per lane) + the default Reply-To.',
})

////////////////////////////////////////////////////////////////////////////////
// Campaign queries (mirror @labcraft/email's campaign wire types)
////////////////////////////////////////////////////////////////////////////////

/**
 * One-line campaign summary. Mirrors the email daemon's
 * `CampaignSummaryWire` (ISO-string dates over the wire).
 *
 * @property id - Campaign id.
 * @property subject - Composed subject.
 * @property classification - Message classification.
 * @property recipientCount - Snapshot recipient-set size.
 * @property suppressedCount - Snapshot suppressed count.
 * @property sentCount - Recipients accepted by the provider.
 * @property failedCount - Recipients whose send failed.
 * @property createdAt - ISO-8601 creation timestamp.
 */
export const CampaignSummarySchema = z.object({
  id: z.string(),
  subject: z.string(),
  classification: z.enum(['marketing', 'transactional']),
  format: z.enum(['text', 'html']),
  recipientCount: z.number().int().nonnegative(),
  suppressedCount: z.number().int().nonnegative(),
  sentCount: z.number().int().nonnegative(),
  deliveredCount: z.number().int().nonnegative(),
  clickedCount: z.number().int().nonnegative(),
  delayedCount: z.number().int().nonnegative(),
  bouncedCount: z.number().int().nonnegative(),
  complainedCount: z.number().int().nonnegative(),
  failedCount: z.number().int().nonnegative(),
  createdAt: z.string(),
})

/** Parsed campaign summary. */
export type CampaignSummary = z.infer<typeof CampaignSummarySchema>

/**
 * One per-recipient delivery record in a campaign detail. Mirrors the
 * email daemon's `CampaignRecipientWire` (send outcome + Part-5 delivery
 * state fed by Resend webhooks).
 *
 * @property recipientId - Account learner id.
 * @property email - Recipient address.
 * @property providerMessageId - Delivery-backend id, or `null` until
 *   sent.
 * @property sendStatus - Send outcome (`pending` / `sent` / `failed`).
 * @property error - Last send error, or `null`.
 * @property deliveryState - Latest delivery state, or `null` until a
 *   webhook lands.
 * @property deliveredAt - ISO timestamp of the `delivered` event, or
 *   `null`.
 * @property bouncedAt - ISO timestamp of the `bounced` event, or `null`.
 * @property complainedAt - ISO timestamp of the `complained` event, or
 *   `null`.
 * @property delayedAt - ISO timestamp of the `delivery_delayed` event, or
 *   `null`.
 * @property clickedAt - ISO timestamp of the first `clicked` event (#1690
 *   Part 4), or `null`.
 * @property clickCount - number of `clicked` events; 0 until the first click.
 */
export const CampaignRecipientSchema = z.object({
  recipientId: z.string(),
  email: z.string(),
  providerMessageId: z.string().nullable(),
  sendStatus: z.enum(['pending', 'sent', 'failed']),
  error: z.string().nullable(),
  deliveryState: z
    .enum(['sent', 'delivery_delayed', 'delivered', 'bounced', 'complained'])
    .nullable()
    .optional(),
  createdAt: z.string().nullable().optional(),
  sentEventAt: z.string().nullable().optional(),
  deliveredAt: z.string().nullable().optional(),
  bouncedAt: z.string().nullable().optional(),
  complainedAt: z.string().nullable().optional(),
  delayedAt: z.string().nullable().optional(),
  clickedAt: z.string().nullable().optional(),
  clickCount: z.number().int().nonnegative().optional(),
})

/** Parsed per-recipient delivery record. */
export type CampaignRecipient = z.infer<typeof CampaignRecipientSchema>

/** Full campaign detail: summary + body + per-recipient delivery records. */
export const CampaignDetailSchema = CampaignSummarySchema.extend({
  body: z.string(),
  recipients: z.array(CampaignRecipientSchema),
})

/** Parsed campaign detail. */
export type CampaignDetail = z.infer<typeof CampaignDetailSchema>

/** Response of `GET /api/emails/campaigns`. */
export const ListCampaignsResponseSchema = z.object({
  campaigns: z.array(CampaignSummarySchema),
})

/** Parsed list-campaigns response. */
export type ListCampaignsResponse = z.infer<typeof ListCampaignsResponseSchema>

/**
 * `GET /api/emails/campaigns` — list campaigns newest-first with send
 * aggregates. Session-gated.
 */
export const ListCampaignsRoute = defineRoute({
  method: 'GET',
  path: '/api/emails/campaigns',
  response: ListCampaignsResponseSchema,
  description: 'List campaigns (newest first) with send aggregates.',
})

/**
 * One unified email event (#1606 Part 10) — a recipient delivery record
 * flattened with its campaign's subject / kind / id, spanning one-off +
 * campaign mail. Mirrors the email daemon's wire shape.
 */
export const EmailEventSchema = CampaignRecipientSchema.extend({
  subject: z.string(),
  type: z.enum(['campaign', 'individual']),
  // `null` for a one-off send (no campaign linkage; #1690 Part 3).
  campaignId: z.string().nullable(),
  sentAt: z.string().nullable(),
})

/** Parsed email event. */
export type EmailEvent = z.infer<typeof EmailEventSchema>

/** Response of `GET /api/emails/events`. */
export const ListEmailEventsResponseSchema = z.object({
  events: z.array(EmailEventSchema),
})

/** Parsed list-email-events response. */
export type ListEmailEventsResponse = z.infer<
  typeof ListEmailEventsResponseSchema
>

/**
 * `GET /api/emails/events` — the unified delivery history (one-off +
 * campaign), newest send first. Session-gated.
 */
export const ListEmailEventsRoute = defineRoute({
  method: 'GET',
  path: '/api/emails/events',
  response: ListEmailEventsResponseSchema,
  description: 'List unified email events (one-off + campaign), newest first.',
})

/** Path params for the per-campaign routes. */
export const CampaignParamsSchema = z.object({ id: z.string().min(1) })

/**
 * `GET /api/emails/campaigns/:id` — one campaign with its per-recipient
 * delivery detail (absorbs #893's delivery table). Session-gated.
 */
export const GetCampaignRoute = defineRoute({
  method: 'GET',
  path: '/api/emails/campaigns/:id',
  params: CampaignParamsSchema,
  response: CampaignDetailSchema,
  description: 'One campaign with per-recipient send + delivery state.',
})

/**
 * `POST /api/emails/campaigns/:id/resend` — re-send the campaign's body
 * to just its bounced / failed recipients (the resend/retry action that
 * absorbs #893's intent). Requestless: the daemon reads the campaign's
 * subject/body + bounced set itself and starts a fresh campaign.
 * Session-gated. Returns the new campaign's tally, or a zero tally when
 * nothing needs re-sending.
 */
export const ResendCampaignRoute = defineRoute({
  method: 'POST',
  path: '/api/emails/campaigns/:id/resend',
  params: CampaignParamsSchema,
  response: SendBroadcastResponseSchema,
  description: 'Re-send a campaign to its bounced/failed recipients.',
})

////////////////////////////////////////////////////////////////////////////////
// Suppressions list (mirror @labcraft/email's SuppressionWire)
////////////////////////////////////////////////////////////////////////////////

/**
 * One suppression row. Mirrors the email daemon's `SuppressionWire`.
 *
 * @property email - Suppressed address.
 * @property reason - Why (`unsubscribe` / `bounce` / `complaint` /
 *   `manual`).
 * @property campaignId - Attribution campaign id, or `null`.
 * @property unsubscribedAt - ISO-8601 timestamp.
 */
export const SuppressionSchema = z.object({
  email: z.string(),
  reason: z.enum(['unsubscribe', 'bounce', 'complaint', 'manual']),
  campaignId: z.string().nullable(),
  unsubscribedAt: z.string(),
})

/** Parsed suppression row. */
export type Suppression = z.infer<typeof SuppressionSchema>

/** Response of `GET /api/emails/suppressions`. */
export const ListSuppressionsResponseSchema = z.object({
  suppressions: z.array(SuppressionSchema),
})

/** Parsed list-suppressions response. */
export type ListSuppressionsResponse = z.infer<
  typeof ListSuppressionsResponseSchema
>

/**
 * `GET /api/emails/suppressions` — list suppressed addresses
 * (newest-opt-out-first). Session-gated.
 */
export const ListSuppressionsRoute = defineRoute({
  method: 'GET',
  path: '/api/emails/suppressions',
  response: ListSuppressionsResponseSchema,
  description: 'List suppressed addresses (newest opt-out first).',
})
